import { msg } from '@lingui/core/macro';
import { formatDocument } from '@/shared/format/document';
import { defineEntitySource, type EntitySource } from '@/shared/form/entity-source';
import { getCurrentTenant } from '@/shared/http/client';
import type * as Api from './person.api';
const listLegalPersonOptions = async (...args: Parameters<typeof Api.listLegalPersonOptions>) =>
  (await import('./person.api')).listLegalPersonOptions(...args);
const listPersonOptions = async (...args: Parameters<typeof Api.listPersonOptions>) =>
  (await import('./person.api')).listPersonOptions(...args);
type Item<F extends (...args: never[]) => Promise<{ data: readonly unknown[] }>> = Awaited<
  ReturnType<F>
>['data'][number];
const doc = (value: string | null | undefined) => (value ? (formatDocument(value) ?? value) : null);
const join = (parts: readonly (string | null | undefined)[]) =>
  parts.filter((part): part is string => typeof part === 'string' && part !== '').join(' · ');

export const administratorSource = defineEntitySource<Item<typeof listLegalPersonOptions>>({
  key: ['person', 'source', 'legal-person'],
  scope: () => getCurrentTenant(),
  search: async (query, signal) =>
    (
      await listLegalPersonOptions(
        { searchString: query || undefined, tenant: getCurrentTenant() },
        signal,
      )
    ).data,
  toOption: (person, i18n) => {
    const partner = person.tenantDisplay ? i18n._(msg`Parceiro: ${person.tenantDisplay}`) : null;
    const cnpj = doc(person.registrationNumber);
    return {
      value: person.id,
      label: person.name,
      description: join([partner, cnpj ? i18n._(msg`CNPJ: ${cnpj}`) : null]),
    };
  },
});
export const personSource = defineEntitySource<Item<typeof listPersonOptions>>({
  key: ['person', 'source', 'person'],
  scope: () => getCurrentTenant(),
  search: async (query, signal) =>
    (
      await listPersonOptions(
        { searchString: query || undefined, tenant: getCurrentTenant() },
        signal,
      )
    ).data,
  toOption: (person, i18n) => {
    const number = doc(person.registrationNumber);
    const alternative = person.alternativeName
      ? person.discriminator === 'LegalPerson'
        ? i18n._(msg`Razão social: ${person.alternativeName}`)
        : i18n._(msg`Nome social: ${person.alternativeName}`)
      : null;
    return {
      value: person.id,
      label: person.name,
      description: join([
        number ? i18n._(msg`CPF/CNPJ: ${number}`) : null,
        person.tenantDisplay ? i18n._(msg`Parceiro: ${person.tenantDisplay}`) : null,
        alternative,
      ]),
    };
  },
});
export const legalPersonSource = administratorSource;
export const naturalPersonSource = defineEntitySource({
  ...personSource,
  key: ['person', 'source', 'natural-person'],
  search: async (query, signal) =>
    (
      await (
        await import('./person.api')
      ).listNaturalPersonOptions(
        { tenant: getCurrentTenant(), searchString: query || undefined },
        signal,
      )
    ).data,
});

const lookups = () => import('./person.api');

/**
 * CNAE (`useEconomicActivityCodeListData`, economicActivityCode.tsx): rótulo `(código) - descrição`, sem
 * busca nenhuma consulta (o legado só liga com texto).
 */
export const economicActivityCodeSource = defineEntitySource<{ readonly id: string; readonly code?: string | null | undefined; readonly description?: string | null | undefined }>({
  key: ['person', 'source', 'economic-activity-code'],
  minQuery: 1,
  search: async (query, signal) => (await lookups()).listEconomicActivityCodes(query, signal),
  toOption: (item) => ({ value: item.id, label: `(${item.code ?? ''}) - ${item.description ?? ''}` }),
});

/** Gestor da empresa (PJ): pessoas físicas, 10 por página, com o tenant da casca (InformationTabContainer.tsx:30-33). */
export const managerSource = defineEntitySource({
  ...personSource,
  key: ['person', 'source', 'manager'],
  search: async (query, signal) => (await (await lookups()).listManagerOptions({ tenant: getCurrentTenant(), searchString: query || undefined }, signal)).data,
});

/** O item que uma fonte devolve (para tipar o `EntityPickerField`). */
export type ItemOf<S> = S extends EntitySource<infer T> ? T : never;
